import type { QueryClient } from "@tanstack/react-query";
import { api } from "./client";
import {
  resolveSuggestionUnit,
  suggestionEvaluations,
  type CreateNoteInput,
  type EditSuggestionInput,
  type ReviewerGroup,
  type Suggestion,
  type SuggestionAction,
  type SuggestionCreateInput,
  type SuggestionHistoryItem,
  type SuggestionListFilters,
  type SuggestionListResponse,
  type SuggestionStats,
  type AssetUploadResponse,
  type PresignedUrlResponse,
} from "../types/ess";

/**
 * Invalidate every React Query cache entry that reflects suggestion
 * state. Centralized here so a new query key (e.g. a future digest view)
 * is added in one place — callers (mutations, switch-user flows) don't
 * need to know about every consumer.
 *
 * Pass `id` to scope the per-suggestion keys; pass `undefined` to skip
 * those (e.g. after a logout / user switch where IDs aren't relevant).
 */
export function invalidateSuggestionCaches(
  qc: QueryClient,
  id?: string,
): void {
  if (id) {
    qc.invalidateQueries({ queryKey: ["ess-detail", id] });
    qc.invalidateQueries({ queryKey: ["ess-history", id] });
    qc.invalidateQueries({ queryKey: ["ess-auth", id] });
    // The delivery log grows by one row per transition, so it goes stale
    // at exactly the same moments the detail does.
    qc.invalidateQueries({ queryKey: ["ess-emails", id] });
  }
  // Prefix invalidation — covers Landing's [ess-list, filters, mine]
  // and AppLayout's filtered-count query.
  qc.invalidateQueries({ queryKey: ["ess-list"] });
  qc.invalidateQueries({ queryKey: ["ess-stats"] });
  // Reports v2 uses its own key prefixes that don't share roots with
  // the Landing-side keys above.
  qc.invalidateQueries({ queryKey: ["ess-list-reports"] });
  qc.invalidateQueries({ queryKey: ["ess-stats-reports"] });
  qc.invalidateQueries({ queryKey: ["ess-stats-reports-prior"] });
}

export async function createSuggestion(
  input: SuggestionCreateInput,
): Promise<Suggestion> {
  const { data } = await api.post<Suggestion>("/ess/", input);
  return data;
}

/**
 * List suggestions. Server-side params (date_from/date_to/my_suggestions)
 * are sent as-is; the multi-select facets (status/category/department/
 * unit/reward_category) are applied client-side against the full result
 * because the endpoint only accepts single values while the UI stores
 * arrays.
 *
 * `unit` and `reward_category` join that same client-side mechanism, but
 * neither is a column the row carries directly:
 *  - unit is resolved via `resolveSuggestionUnit` — `metadata.unit` on
 *    rows created after the unit field landed, else `unitByDepartment`
 *    (a department -> unit lookup the caller supplies, built from the
 *    `/departments/` catalog). This function has no way to fetch that
 *    catalog itself (it's a plain async function, not a hook), so the
 *    caller — the view that already calls `useDepartments()` for its own
 *    filter dropdown — passes the resolved lookup straight through.
 *  - reward_category lives inside `metadata.evaluations.ssc`, resolved via
 *    `suggestionEvaluations(row).ssc?.reward_category`.
 */
export async function listSuggestions(
  filters: SuggestionListFilters = {},
  unitByDepartment: Record<string, string> = {},
): Promise<SuggestionListResponse> {
  const {
    status,
    category,
    department,
    unit,
    reward_category,
    ...serverFilters
  } = filters;
  const params = Object.fromEntries(
    Object.entries(serverFilters).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );
  const { data } = await api.get<SuggestionListResponse>("/ess/", { params });

  const statusSet = status && status.length ? new Set(status) : null;
  const categorySet = category && category.length ? new Set(category) : null;
  const departmentSet =
    department && department.length ? new Set(department) : null;
  const unitSet = unit && unit.length ? new Set(unit) : null;
  const rewardSet =
    reward_category && reward_category.length
      ? new Set(reward_category)
      : null;
  if (!statusSet && !categorySet && !departmentSet && !unitSet && !rewardSet)
    return data;

  const filtered = data.suggestions.filter((s) => {
    if (statusSet && !statusSet.has(s.status)) return false;
    if (categorySet && !categorySet.has(s.category)) return false;
    if (departmentSet && !departmentSet.has(s.department)) return false;
    if (unitSet) {
      const resolved = resolveSuggestionUnit(s, unitByDepartment);
      if (!resolved || !unitSet.has(resolved)) return false;
    }
    if (rewardSet) {
      const reward = suggestionEvaluations(s).ssc?.reward_category;
      if (!reward || !rewardSet.has(reward)) return false;
    }
    return true;
  });
  return { suggestions: filtered, total: filtered.length };
}

export async function getSuggestion(id: string): Promise<Suggestion> {
  const { data } = await api.get<Suggestion>(`/ess/${id}`);
  return data;
}

export async function updateSuggestionStatus(
  id: string,
  action: SuggestionAction,
  remarks?: string,
  // Optional action-specific payload. Used by reviewer-assignment
  // self-transitions to pass { user_id, committee, sub_role } or
  // { from_user_id, to_user_id }. State transitions ignore this.
  params?: Record<string, unknown>,
): Promise<Suggestion> {
  const { data } = await api.put<Suggestion>(`/ess/${id}/status`, {
    action,
    remarks,
    params,
  });
  return data;
}

/**
 * Edit a suggestion's content during HR review. Only fields the user
 * actually changed are sent so the audit trail records the diff. Auth
 * is enforced server-side by the yaml `edit_suggestion` predicate;
 * the caller must invalidate suggestion caches via
 * `invalidateSuggestionCaches` after the response.
 */
export async function editSuggestion(
  id: string,
  input: EditSuggestionInput,
): Promise<Suggestion> {
  const { data } = await api.patch<Suggestion>(`/ess/${id}`, input);
  return data;
}

/**
 * Append a note (text + optional attachments) to a suggestion. Returns
 * the updated suggestion with the new note in `notes`. Auth is enforced
 * server-side by the yaml `add_note` predicate; the caller must
 * invalidate suggestion caches via `invalidateSuggestionCaches` after.
 */
export async function addNote(
  id: string,
  input: CreateNoteInput,
): Promise<Suggestion> {
  const { data } = await api.post<Suggestion>(`/ess/${id}/notes`, input);
  return data;
}

/**
 * Submit a WC (Work Committee) evaluation for a suggestion. Thin wrapper
 * over `updateSuggestionStatus` — the evaluation payload (scores, total,
 * outcome, etc. — see `EvaluationRecord`) rides in `params`; the backend
 * `submit_wc_evaluation` action stores it under `metadata.evaluations.wc`.
 */
export async function submitWcEvaluation(
  id: string,
  params: Record<string, unknown>,
): Promise<Suggestion> {
  return updateSuggestionStatus(id, "submit_wc_evaluation", undefined, params);
}

/**
 * Submit an SC (Steering/Sub Committee) evaluation for a suggestion.
 * Mirrors `submitWcEvaluation` — the backend `submit_ssc_evaluation`
 * action stores the payload under `metadata.evaluations.ssc`.
 */
export async function submitSscEvaluation(
  id: string,
  params: Record<string, unknown>,
): Promise<Suggestion> {
  return updateSuggestionStatus(
    id,
    "submit_ssc_evaluation",
    undefined,
    params,
  );
}

/**
 * Assign (or clear) one committee slot. Thin wrapper over
 * `updateSuggestionStatus` — `assign_reviewers` is a self-transition on
 * `submitted`, so the row's status doesn't change; the backend writes the
 * entry into `metadata.assigned_reviewers`. A null `user_id` clears the
 * slot. Slot keys come from the workflow config's `committees` block.
 */
export async function assignReviewer(
  id: string,
  params: { committee: string; slot: string; user_id: string | null },
): Promise<Suggestion> {
  return updateSuggestionStatus(id, "assign_reviewers", undefined, params);
}

/**
 * Record one verifier department's implementation, or add a comment to
 * its thread. `record_department_implementation` is a self-transition on
 * `accepted_by_sc`, so the row's status doesn't change here — the backend
 * moves the suggestion to `implemented` on its own once every nominated
 * department is marked done. Pass `implemented` to set the flag,
 * `comment` to append to the thread, or both.
 */
export async function recordDepartmentImplementation(
  id: string,
  params: { department: string; implemented?: boolean; comment?: string },
): Promise<Suggestion> {
  return updateSuggestionStatus(
    id,
    "record_department_implementation",
    undefined,
    params,
  );
}

export async function getSuggestionHistory(
  id: string,
): Promise<SuggestionHistoryItem[]> {
  const { data } = await api.get<SuggestionHistoryItem[]>(`/ess/${id}/history`);
  return data;
}

export async function getSuggestionStats(
  params: {
    date_from?: string;
    date_to?: string;
    department?: string;
  } = {},
): Promise<SuggestionStats> {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
  );
  const { data } = await api.get<SuggestionStats>("/ess/stats", {
    params: clean,
  });
  return data;
}

/**
 * Fetch the WC reviewer group for an ESS category. Returns null on 404
 * (no group exists yet) so the assign-reviewer card can fall back to
 * its manual picker. Other errors propagate.
 */
export async function getReviewerGroup(
  category: string,
): Promise<ReviewerGroup | null> {
  try {
    const { data } = await api.get<ReviewerGroup>(
      `/ess/reviewer-groups/${encodeURIComponent(category)}`,
    );
    return data;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
}

export async function uploadAsset(file: File): Promise<AssetUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<AssetUploadResponse>("/assets/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function getAssetUrl(
  assetId: string,
): Promise<PresignedUrlResponse> {
  const { data } = await api.get<PresignedUrlResponse>(
    `/assets/${assetId}/get_presigned_url`,
  );
  return data;
}
